<template>
  <div class="auth-form__form-wrapper">
    <form
      :class="[
        'ine-form__form',
        'auth-form__form',
        !loginWithPersonal && 'auth-form__form--company',
      ]"
      @submit.prevent="validateFields"
    >
      <div v-if="bannerEnabled" class="login__error-banner">
        Thank you for your patience as we are currently working to improve our
        site.
      </div>
      <Title
        class="auth-form__title-wrapper"
        header="Sign in to your INE account"
      />
      <div v-if="loginWithPersonal" class="auth-form__form-group">
        <div
          v-for="(field, key) in fields"
          v-show="field.type"
          :key="`field-${key}`"
          class="auth-form__input-wrapper"
        >
          <input
            v-if="field.type"
            v-model="field.value"
            :type="field.type"
            :class="[
              'auth-form__form-control',
              'ine-input',
              errors.fields.includes(key) && 'ine-input--error',
            ]"
            :placeholder="field.placeholder"
            :autocomplete="field.autocomplete"
            @focus="clearErrors"
          />
          <Errors v-if="errors.fields.includes(key)" :errors="errors[key]" />
        </div>
      </div>
      <div v-else class="auth-form__form-group">
        <input
          v-model="companyName"
          type="text"
          class="auth-form__form-control ine-input"
          placeholder="Company Name"
          @focus="clearErrors"
        />
      </div>
      <div class="auth-form__actions">
        <div class="auth-form__actions-group">
          <SubmitBtn
            class="auth-form__actions-submit-wrapper auth-form__actions-submit-wrapper--login"
            extraButtonClasses="auth-form__actions__submit"
            title="Sign In"
            :disabled="!canSubmit"
          />
          <router-link
            v-if="loginWithPersonal"
            :to="{ name: 'forgot-password' }"
          >
            <button class="btn btn--tertiary">Forgot your password?</button>
          </router-link>
        </div>
        <div class="auth-form__actions-group">
          <button
            v-if="canResendConfirmationEmail"
            class="btn btn--tertiary"
            :disabled="isLoading"
            @click.prevent="resendConfirmationEmail()"
          >
            Resend Confirmation Email
          </button>
        </div>
        <div class="auth-form__oauth">
          <span class="auth-form__oauth-separator">OR</span>
        </div>
        <div class="auth-form__providers-wrapper">
          <button
            v-for="provider in authProviders"
            :key="`provider-${provider.id}`"
            class="btn btn--secondary btn--default-icon-color"
            :disabled="isLoading"
            @click.prevent="$emit('login-through-provider', provider)"
          >
            <component :is="getProviderIcon(provider.name)" />
            Log In with {{ provider.name }}
          </button>
          <button
            :key="'provider-company'"
            class="btn btn--secondary btn--default-icon-color auth-form__sso-button"
            :disabled="isLoading"
            @click.prevent="togglePersonal"
          >
            Sign in with your {{ account }}
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { validateForm, clearFormErrors } from '@/helpers'
import Title from '@/components/forms/elements/Title.vue'
import Errors from '@/components/forms/elements/Errors.vue'
import SubmitBtn from '@/components/forms/elements/SubmitBtn.vue'
import GoogleIcon from '@/assets/icons/fill/google.svg'
import Spinner from '@/components/loaders/LoadingSpinnerV2.vue'

const ICONS_MAPPING = {
  Google: GoogleIcon,
}

export default {
  name: 'LoginForm',
  components: {
    Title,
    Errors,
    SubmitBtn,
    Spinner,
  },
  props: {
    isLoading: {
      type: Boolean,
      required: true,
    },
    isAuthenticated: {
      type: Boolean,
      required: true,
    },
    showResendEmail: {
      type: Boolean,
      required: true,
    },
    authProviders: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      errors: clearFormErrors(),
      fields: {
        email: {
          type: 'email',
          placeholder: 'Email',
          rules: ['presence', 'email'],
          value: '',
          autocomplete: 'email',
        },
        password: {
          type: 'password',
          placeholder: 'Password',
          rules: ['presence'],
          value: '',
          autocomplete: 'current-password',
        },
      },
      loginWithPersonal: true,
      companyName: null,
    }
  },
  computed: {
    account() {
      return this.loginWithPersonal ? 'company' : 'personal account'
    },
    bannerEnabled() {
      return process.env.VUE_APP_ERROR_BANNER_LOGIN
    },
    canResendConfirmationEmail() {
      return (
        !this.isAuthenticated && this.loginWithPersonal && this.showResendEmail
      )
    },
    canSubmit() {
      return this.loginWithPersonal
        ? this.fields.email.value && this.fields.password.value
        : this.companyName?.length
    },
  },
  methods: {
    async validateFields() {
      const { fields } = this
      if (!this.loginWithPersonal) {
        this.$emit('submit-company', this.companyName)
        return
      }
      const { isValid, errors } = validateForm(fields)
      this.errors = errors
      if (isValid) {
        const payload = {
          email: fields.email.value,
          password: fields.password.value,
        }
        this.$emit('submit-form', payload)
      }
    },
    clearErrors() {
      this.errors = clearFormErrors()
    },
    getProviderIcon(providerName) {
      return ICONS_MAPPING[providerName]
    },
    togglePersonal() {
      this.loginWithPersonal = !this.loginWithPersonal
    },
    async resendConfirmationEmail() {
      const email = this.fields.email?.value
      if (email) {
        this.$emit('resend-loading', true)
        try {
          await this.$store.dispatch('auth/resendConfirmationEmail', email)
          this.$store.dispatch(
            'app/setSuccess',
            'The confirmation email has been resent.'
          )
        } finally {
          this.$emit('resend-loading', false)
        }
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.login {
  &__sso-button,
  &__resend-button {
    margin-top: 20px;
  }
  &__error-banner {
    position: absolute;
    top: 30px;
    width: 80%;
    margin: 0 0 5px 0;
    padding: 25px 15px;
    color: white;
    background: linear-gradient(
      270deg,
      var(--bg-color-1, #cf2121) 0%,
      var(--bg-color-2, #860606) 100%
    );
    border-radius: 10px;
    will-change: opacity, transform;
    animation: appearenceAnim 0.5s forwards;
    cursor: pointer;
    &__inner {
      position: relative;
      padding-left: 40px;
      padding-right: 32px;
    }
    &__icon {
      position: absolute;
      left: 0;
      top: 50%;
      height: 20px;
      width: 20px;
      margin-top: -10px;
    }
    &__text {
      flex-grow: 1;
      margin: 0;
      font-size: 16px;
      line-height: 23px;
      color: #fff;
    }
  }
}
</style>
